// useAccessToken.ts
import { useEffect, useState, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, type AccountInfo } from "@azure/msal-browser";
import { loginRequest } from "./authConfig";
import { useAppDispatch } from "./app/hooks";
import { DISABLE_AUTH } from "./flags";
import { setAccessToken } from "./globalSlice";

type Result = {
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export function useAccessToken(): Result {
  const { instance } = useMsal();
  const [accessToken, setTokenLocal] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const dispatch = useAppDispatch();

  // ⬇️ DEV-BYPASS
  if (DISABLE_AUTH) {
    dispatch(setAccessToken({ accessToken: "dev-token" }));
    return {
      accessToken: "dev-token",
      isLoading: false,
      error: null,
      refresh: async () => {},
    };
  }

  // undvik state-updates efter unmount
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const setSafe = (fn: () => void) => {
    if (mounted.current) fn();
  };

  const fetchToken = useCallback(async () => {
    setSafe(() => {
      setLoading(true);
      setError(null);
    });

    try {
      // 1) Se till att det finns ett aktivt konto i cachen
      let acc = instance.getActiveAccount() as AccountInfo | null;
      if (!acc) {
        const all = instance.getAllAccounts();
        if (all[0]) {
          instance.setActiveAccount(all[0]);
          acc = all[0] as AccountInfo;
        }
      }

      // 2) Om inget konto: försök silent SSO EN gång
      if (!acc) {
        try {
          await instance.ssoSilent(loginRequest);
        } catch (e) {
          // Ingen SSO-session → vi har ingen token just nu
          setSafe(() => {
            setTokenLocal(null);
            setError(null); // inget fel – bara inte inloggad ännu
            setLoading(false);
          });
          return; // låt UI:n hantera redirect eller visa login-knapp
        }
      }

      const account = (instance.getActiveAccount() || instance.getAllAccounts()[0]) as
        | AccountInfo
        | undefined;
      if (!account) {
        setSafe(() => {
          setTokenLocal(null);
          setError("No account available after ssoSilent");
          setLoading(false);
        });
        return;
      }

      // 3) Hämta token tyst
      const res = await instance.acquireTokenSilent({ ...loginRequest, account });
      setSafe(() => {
        setTokenLocal(res.accessToken);
        setLoading(false);
        dispatch(
          setAccessToken({
            accessToken: res.accessToken,
            expiresOn: res.expiresOn?.toISOString() ?? null,
          })
        );
      });
    } catch (e: any) {
      if (e instanceof InteractionRequiredAuthError) {
        // Vi är inte inloggade (eller samtycke krävs).
        // Sätt loading=false så UI kan besluta om redirect visuellt.
        setSafe(() => {
          setTokenLocal(null);
          setError(null);
          setLoading(false);
        });
        return;
      }
      console.error("acquireTokenSilent failed:", e);
      setSafe(() => {
        setTokenLocal(null);
        setError(e?.errorCode || e?.message || "acquireTokenSilent failed");
        setLoading(false);
      });
    }
  }, [instance]);

  useEffect(() => {
    // Kör vid mount
    fetchToken();
    // Förnya var 5:e minut (bara om hooken fortfarande är monterad)
    const h = setInterval(fetchToken, 5 * 60 * 1000);
    return () => clearInterval(h);
  }, [fetchToken]);

  return { accessToken, isLoading, error, refresh: fetchToken };
}
